import { Link } from "react-router-dom";
import { ShieldCheck, Mail, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "February 1, 2026";
const CONTACT_EMAIL = "privacy@hoomanz.co";

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 prose-hoomanz" data-testid="privacy-page">
      <Link to="/" className="text-sm text-[#4A4A4A] inline-flex items-center gap-1 hover:text-[#0A0A0A] mb-6">
        <ArrowLeft size={14}/> Back home
      </Link>

      <div className="flex items-center gap-3 mb-4">
        <span className="h-10 w-10 rounded-full bg-[#0A0A0A] text-white inline-flex items-center justify-center">
          <ShieldCheck size={18}/>
        </span>
        <div>
          <p className="overline">Legal</p>
          <h1 className="h-display text-4xl">Privacy Policy</h1>
        </div>
      </div>
      <p className="text-sm text-[#8A8A8A] mb-10">Last updated: {LAST_UPDATED}</p>

      <Section title="1. Who we are">
        <p>
          Hoomanz (&ldquo;<strong>Hoomanz</strong>,&rdquo; &ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>,&rdquo; &ldquo;<strong>our</strong>&rdquo;) operates the marketplace at
          {" "}<a href="https://hoomanz.co" className="underline">hoomanz.co</a>{" "}
          where independent people (&ldquo;<strong>Hoomans</strong>&rdquo;) offer services, digital goods,
          physical items, jobs, and rentals to one another. This Privacy Policy explains what we collect,
          how we use it, who we share it with, how we secure it, and the rights you have.
        </p>
        <p>
          By using Hoomanz you agree to the practices described here. If you do not agree, please do not use the
          service.
        </p>
      </Section>

      <Section title="2. Information we collect">
        <SubHeading>Information you give us</SubHeading>
        <ul>
          <li><strong>Account & profile:</strong> name, email, password (hashed with bcrypt — we never store it in plain text), profile photo, bio, location, languages, categories/professions, headline, links, verification status.</li>
          <li><strong>Listings & content:</strong> services, products, jobs, for-sale items, rentals, posts, photos, videos, reviews, replies, messages.</li>
          <li><strong>Bookings & orders:</strong> dates, durations, addresses, delivery preferences, applicant messages, rental check-in/out info.</li>
          <li><strong>Payout details:</strong> bank account, Stripe Connect onboarding identifiers, PayPal/Venmo/Cash App handles, Wise emails. Sensitive payout fields are encrypted at rest with a server-side key and never shown back in plaintext after entry.</li>
          <li><strong>Communications with us:</strong> support requests, dispute notes, feedback.</li>
        </ul>

        <SubHeading>Information we collect automatically</SubHeading>
        <ul>
          <li><strong>Device & log data:</strong> IP address, browser type, operating system, referring/exit URLs, pages viewed, clicks, timestamps, error logs.</li>
          <li><strong>Cookies & local storage:</strong> a session token (JWT) so you stay signed in, a language preference, and a few UI flags. We do not use third-party advertising cookies.</li>
          <li><strong>Stripe payment metadata:</strong> when you pay or get paid, Stripe returns a session ID, payment intent ID, and status code — we store those for receipt and reconciliation purposes.</li>
        </ul>

        <SubHeading>Information from third parties</SubHeading>
        <ul>
          <li><strong>Google Sign-In:</strong> if you choose Google login, we receive your name, email, and a Google account identifier.</li>
          <li><strong>Stripe:</strong> after a successful checkout we receive payment status, last-4 of the card, country, and the platform fee Stripe collected on our behalf.</li>
        </ul>
      </Section>

      <Section title="3. How we use your information">
        <ul>
          <li>To create and maintain your account, profile, and identities.</li>
          <li>To match customers and Hoomans — show your listings on Discover, Jobs, Market, Rentals, and the Shop.</li>
          <li>To process bookings, accept payments through Stripe Connect (a 2 % Hoomanz Tip added on top of each transaction), and pay out 100 % of the seller&apos;s price to the Hooman who earned it.</li>
          <li>To generate the secure Jitsi video room for online consultation bookings.</li>
          <li>To send transactional emails (booking confirmations, payout updates, dispute notices) and in-app notifications. We do <em>not</em> send marketing emails without your separate, explicit opt-in.</li>
          <li>To prevent fraud, detect abusive behaviour, and enforce our Community Guidelines.</li>
          <li>To translate listings and reviews into the viewer&apos;s language using Anthropic Claude (only the public text content is sent; no account identifiers).</li>
          <li>To improve the product — anonymous, aggregated analytics about which surfaces are used.</li>
          <li>To comply with legal obligations (tax reporting, lawful requests from authorities, anti-money-laundering checks performed by Stripe).</li>
        </ul>
      </Section>

      <Section title="4. Who we share information with">
        <p>We share the minimum amount of data needed, only with the parties below, and never sell or rent personal information to advertisers or data brokers.</p>

        <SubHeading>Other Hoomans on the platform</SubHeading>
        <ul>
          <li>Your <strong>public profile</strong> (name, photo, bio, categories, location city, listings, reviews, follow counts) is visible to anyone who visits your profile page.</li>
          <li>Your <strong>contact details and full address</strong> are <em>not</em> publicly visible — they are only shared with the counterpart of a confirmed booking, order, or rental.</li>
          <li>When you book or buy, the seller sees your display name, profile photo, and the message you sent — never your password, payout details, or full email unless you put it in a message.</li>
        </ul>

        <SubHeading>Service providers (data processors)</SubHeading>
        <ul>
          <li><strong>Stripe, Inc.</strong> — payment processing and Connect payouts. Stripe collects card numbers and bank details directly; Hoomanz never sees or stores raw card numbers.</li>
          <li><strong>Anthropic (Claude)</strong> — translation of listing/review text. Only the public text is sent. <a href="https://www.anthropic.com/legal/privacy" className="underline" target="_blank" rel="noreferrer">Anthropic Privacy</a>.</li>
          <li><strong>Google Cloud / Google Sign-In</strong> — optional federated login. <a href="https://policies.google.com/privacy" className="underline" target="_blank" rel="noreferrer">Google Privacy</a>.</li>
          <li><strong>Jitsi (8x8, Inc.)</strong> — peer-to-peer encrypted video rooms for online consultations. No recording is stored by Hoomanz.</li>
          <li><strong>SendGrid (Twilio, Inc.)</strong> — transactional email delivery (receipts, password resets, dispute notices).</li>
          <li><strong>MongoDB Atlas / Emergent cloud infrastructure</strong> — hosts the application database and servers in encrypted form.</li>
        </ul>

        <SubHeading>Legal & safety</SubHeading>
        <ul>
          <li>We will share information when compelled by valid legal process (subpoena, court order, lawful government request) or when necessary to protect the safety, rights, or property of Hoomanz, our users, or the public.</li>
          <li>In the event of a merger, acquisition, or sale of all or substantially all of our assets, your information may be transferred — we&apos;ll notify you in advance and you&apos;ll have the chance to delete your account first.</li>
        </ul>

        <SubHeading>How disclosure happens (method)</SubHeading>
        <p>
          All transfers to service providers happen over TLS 1.2+ encrypted HTTPS API calls. We use the
          minimum-permissions API tokens provided by each vendor and rotate them on schedule. Disclosures in
          response to legal process are reviewed by a human first; we challenge over-broad requests where the law
          permits.
        </p>
      </Section>

      <Section title="5. How we protect your information">
        <ul>
          <li><strong>Encryption in transit:</strong> the entire site, every API call, and every third-party integration runs over HTTPS / TLS 1.2+.</li>
          <li><strong>Encryption at rest:</strong> the database is encrypted at the disk level; sensitive payout fields (bank account, IBAN, etc.) are additionally encrypted with an application-layer key tied to your Hoomanz user.</li>
          <li><strong>Passwords:</strong> hashed with <code>bcrypt</code> (cost factor 12). We literally cannot read your password — even we don&apos;t know what it is.</li>
          <li><strong>Authentication:</strong> JWT session tokens with short expiry; step-up re-authentication is required to view or change payout details.</li>
          <li><strong>Payments:</strong> credit card numbers are entered directly into a Stripe-hosted checkout page and never touch our servers. Hoomanz is therefore in scope only for SAQ-A of PCI-DSS.</li>
          <li><strong>Least privilege access:</strong> production database access is restricted to a small group of engineers, audited, and gated behind 2FA.</li>
          <li><strong>Brute-force protection:</strong> repeated failed login attempts are rate-limited.</li>
          <li><strong>Continuous monitoring:</strong> error logs and abnormal request patterns trigger alerts.</li>
          <li><strong>Backup & recovery:</strong> the database is backed up daily and restorable to a point in time.</li>
        </ul>
        <p className="mt-3 text-sm text-[#4A4A4A]">
          No system is 100% secure. If we ever detect a breach affecting your data, we will notify you and the
          relevant authorities within 72 hours of discovery, as required by GDPR Art. 33 and applicable U.S.
          state laws.
        </p>
      </Section>

      <Section title="6. Cookies & similar technologies">
        <p>Hoomanz uses only the cookies and local-storage entries strictly needed to run the site:</p>
        <ul>
          <li><strong>Session token</strong> (HttpOnly when possible) — keeps you signed in.</li>
          <li><strong>Language preference</strong> — remembers your chosen interface language.</li>
          <li><strong>UI flags</strong> — remember whether you&apos;ve dismissed certain onboarding hints.</li>
        </ul>
        <p>We do not use third-party advertising or analytics cookies. You can clear them at any time from your browser settings.</p>
      </Section>

      <Section title="7. Data retention">
        <ul>
          <li>Active account data is retained while your account exists.</li>
          <li>If you delete your account, profile, posts, listings and personal data are removed within 30 days, except for: (a) transaction records we are legally required to keep for tax / accounting (7 years), and (b) anonymised analytics that no longer identify you.</li>
          <li>Booking and payment records are retained for the duration legally required by tax and consumer-protection law in your jurisdiction.</li>
          <li>Translated content cached for performance is auto-purged after 90 days of inactivity.</li>
        </ul>
      </Section>

      <Section title="8. Your rights">
        <p>Depending on where you live, you may have one or more of these rights. We honor them for all users globally as a matter of policy:</p>
        <ul>
          <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
          <li><strong>Correction</strong> — update inaccurate or incomplete information (most fields are editable directly in your profile settings).</li>
          <li><strong>Deletion</strong> — delete your account and personal data (subject to legal retention exceptions above).</li>
          <li><strong>Portability</strong> — export your data in a structured, machine-readable JSON format.</li>
          <li><strong>Objection / restriction</strong> — opt out of specific processing (e.g., turn off translation).</li>
          <li><strong>Withdraw consent</strong> — for anything we process on consent (e.g., marketing if you ever opt in).</li>
          <li><strong>Complain</strong> — to your local data protection authority (e.g., your EU DPA, the UK ICO, or your state Attorney General).</li>
        </ul>
        <p>To exercise any right, email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>. We respond within 30 days.</p>
      </Section>

      <Section title="9. Children">
        <p>Hoomanz is not directed to children under 16. We do not knowingly collect data from anyone under 16. If you believe a child has provided data to us, please email us and we will delete it.</p>
      </Section>

      <Section title="10. International users">
        <p>Hoomanz operates globally. Your data may be processed in countries outside your own (typically the United States and the European Union). Where required, we use the appropriate transfer safeguards — Standard Contractual Clauses for EU/UK transfers and equivalent mechanisms elsewhere.</p>
      </Section>

      <Section title="11. Changes to this policy">
        <p>If we make material changes, we will email you and post a banner on the site at least 7 days before the new terms take effect.</p>
      </Section>

      <Section title="12. Contact us">
        <p>Questions, requests, or concerns? Reach our privacy team directly:</p>
        <p className="text-[#0A0A0A] inline-flex items-center gap-2">
          <Mail size={14}/> <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
        </p>
      </Section>

      <p className="text-xs text-[#8A8A8A] mt-12 pt-6 border-t border-[#E5E5E3]">
        This Privacy Policy is provided in plain English so you actually read it. It does not constitute legal
        advice. If you need a specific legal interpretation, consult a qualified lawyer in your jurisdiction.
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10" data-testid={`privacy-section-${title.split(".")[0]}`}>
      <h2 className="font-heading text-2xl text-[#0A0A0A] mb-3">{title}</h2>
      <div className="text-[15px] leading-relaxed text-[#0A0A0A] [&>p]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-3 [&>ul>li]:mb-1.5">
        {children}
      </div>
    </section>
  );
}

function SubHeading({ children }) {
  return <h3 className="font-medium text-[15px] mt-4 mb-2 text-[#0A0A0A]">{children}</h3>;
}
